import { Routes, Route, Navigate } from "react-router-dom";
import { useMemo } from "react";
import Applications from "../pages/tam/Applications";
import Users from "../pages/admin/Users";
import InternalProjects from "../pages/csm/Projects";
import ProjectList from "../pages/project/ProjectList";
import ProjectCreate from "../pages/project/ProjectCreate";
import ProjectDetails from "../pages/project/ProjectDetails";
import ProjectEdit from "../pages/project/ProjectEdit";
import Settings from "../pages/settings/Settings";
import { useAuthProvider } from "../auth/hooks/auth-provider";
import { UserRole } from "../models/UserRole";
import CompanyList from "../pages/internal/CompanyList";
import CompanyCreate from "../pages/internal/CompanyCreate";
import CompanyEdit from "../pages/internal/CompanyEdit";
import CompanyDetails from "../pages/internal/CompanyDetails";
import AuditLogs from "../pages/admin/AuditLogs";
import FreelancerExport from "../pages/admin/FreelancerExport";
import AnalyticsDashboard from "../pages/analytics/Dashboard";
import ClientList from "../pages/client/Clients";
import ClientDetails from "../pages/client/Client";
import ClientEdit from "../pages/client/ClientEdit";
import ApplicationDetails from "../pages/tam/ApplicationDetails";
import FreelancerOnboardingForms from "../pages/freelancer/FreelancerOnboardingForms";
import CompanyGroupList from "../pages/internal/CompanyGroupList";
import CompanyGroupCreate from "../pages/internal/CompanyGroupCreate";
import CompanyGroupEdit from "../pages/internal/CompanyGroupEdit";
import CompanyGroupDetails from "../pages/internal/CompanyGroupDetails";
import Evaluations from "../pages/csm/Evaluations";
import TalentSearch from "../pages/csm/TalentSearch";
import TalentSelectionsList from "../pages/csm/TalentSelectionsList";
import TalentSelectionDetail from "../pages/csm/TalentSelectionDetail";
import NewProspectSelection from "../pages/csm/NewProspectSelection";
import TalentAnonymizedPreview from "../pages/csm/TalentAnonymizedPreview";
import InternalUserProfile from "../pages/internal/InternalUserProfile";
import CommunityDashboard from "../pages/internal/CommunityDashboard";

const InternalRoutes: React.FC = () => {
	const { user } = useAuthProvider();

	// Use useMemo to avoid recalculating on every render
	const { hasAdminRole, hasTAMRole, hasCSMRole, isInternalUser, defaultRedirect } =
		useMemo(() => {
			const hasAdminRole = user.roles?.includes(UserRole.Admin) || false;
			const hasTAMRole = user.roles?.includes(UserRole.TAM) || false;
			const hasCSMRole = user.roles?.includes(UserRole.CSM) || false;
			const isInternalUser = hasAdminRole || hasTAMRole || hasCSMRole;

			// Calculate default redirect path based on role
			// Fixed default routes per role - no localStorage override
			let defaultRedirect = "";

			if (hasTAMRole) {
				defaultRedirect = "/applications";
			} else if (hasCSMRole) {
				defaultRedirect = "/projects/all";
			} else if (hasAdminRole) {
				defaultRedirect = "/users";
			}

			return { hasAdminRole, hasTAMRole, hasCSMRole, isInternalUser, defaultRedirect };
		}, [user]);

	return (
		<Routes>
			{/* Only include routes the user has access to */}
			{hasTAMRole && <Route path="applications" element={<Applications />} />}
			{(hasTAMRole || hasCSMRole) && (
				<Route path={"profile/:key"} element={<FreelancerOnboardingForms />} />
			)}

			{/* Talent details accessible to all internal users (TAM, CSM, Admin) */}
			{isInternalUser && (
				<Route path="applications/talent/:key" element={<ApplicationDetails />} />
			)}
			{hasAdminRole && <Route path="users" element={<Users />} />}
			{hasAdminRole && <Route path="audit-logs" element={<AuditLogs />} />}
			{(hasAdminRole || hasTAMRole) && <Route path="analytics" element={<AnalyticsDashboard />} />}
			{(hasAdminRole || hasTAMRole || hasCSMRole) && (
				<Route path="community" element={<CommunityDashboard />} />
			)}
			{hasAdminRole && <Route path="freelancer-export" element={<FreelancerExport />} />}
			{hasCSMRole && <Route path="projects" element={<InternalProjects />} />}

			{/* Project management routes - accessible to CSM and Admin */}
			{(hasCSMRole || hasAdminRole) && (
				<>
					<Route path="projects/all" element={<ProjectList />} />
					<Route path="projects/create" element={<ProjectCreate />} />
					<Route path="projects/:id" element={<ProjectDetails />} />
					<Route path="projects/:id/edit" element={<ProjectEdit />} />
				</>
			)}

			{/* Company routes - only render if user has access */}
			{isInternalUser && (
				<>
					<Route path="companies/create" element={<CompanyCreate />} />
					<Route path="companies/:id/edit" element={<CompanyEdit />} />
					<Route path="companies/:id" element={<CompanyDetails />} />
					<Route path="companies" element={<CompanyList />} />
				</>
			)}

			{/* Company Group routes */}
			{isInternalUser && (
				<>
					<Route path="company-groups/create" element={<CompanyGroupCreate />} />
					<Route path="company-groups/:id/edit" element={<CompanyGroupEdit />} />
					<Route path="company-groups/:id" element={<CompanyGroupDetails />} />
					<Route path="company-groups" element={<CompanyGroupList />} />
				</>
			)}

			{/* Client routes - only accessible to Admin and CSM */}
			{(hasAdminRole || hasCSMRole) && (
				<>
					<Route path="clients/:id/edit" element={<ClientEdit />} />
					<Route path="clients/:id" element={<ClientDetails />} />
					<Route path="clients" element={<ClientList />} />
				</>
			)}

			{/* Talent search route - accessible to all internal users */}
			{isInternalUser && (
				<Route path="talent-search" element={<TalentSearch />} />
			)}

			{/* Saved talent selections (Option B) — internal users only.
			    The list page surfaces every selection the user saved from the cart,
			    the detail page exposes per-link actions (copy URL, revoke).
			    `talent-prospection/new` is the dedicated wizard for outbound
			    prospecting (Option B) — same building blocks as `/talent-search`
			    but framed for the commercial use case.
			    The daily client/talent matcher's audit lives inline on the
			    client detail page (`/clients/:id`), so there is no dedicated
			    route here. */}
			{isInternalUser && (
				<>
					<Route path="talent-prospection/new" element={<NewProspectSelection />} />
					<Route path="talent-selections" element={<TalentSelectionsList />} />
					<Route path="talent-selections/:id" element={<TalentSelectionDetail />} />
					{/* "Preview as the client sees it" — opened from the matched-talents
					    grid on the client detail page. The optional ?clientId= query
					    string carries the originating client id for the back link. */}
					<Route
						path="talent-anonymized-preview/:freelancerProfileId"
						element={<TalentAnonymizedPreview />}
					/>
				</>
			)}

			{/* Evaluations route - accessible to CSM and Admin */}
			{(hasCSMRole || hasAdminRole) && (
				<Route path="evaluations" element={<Evaluations />} />
			)}

			{/* Settings route - accessible to all internal users */}
			{isInternalUser && <Route path="settings" element={<Settings />} />}

			{/* Internal user profile (PhoneNumber, JobTitle, Calendly URLs surfaced in AM emails).
			    Accessible to all internal users — they see their own profile by default, and
			    Admins can pick any internal user to edit via the in-page picker. */}
			{isInternalUser && (
				<Route path="internal-profile" element={<InternalUserProfile />} />
			)}

			{/* Default redirect - use pre-calculated path */}
			<Route path="*" element={<Navigate to={defaultRedirect} />} />
		</Routes>
	);
};

export default InternalRoutes;
